/**
 * CZNA-554 — remembers, per user, the newest review-queue item the supervisor has already
 * seen, so the landing page can say "something new arrived" rather than only "N waiting".
 *
 * <p>The marker is the server's `waitingSince` of the newest item the queue showed when it
 * was opened — never the browser clock, so a skewed client clock cannot hide a new item.
 * It only moves forward.
 *
 * <p>localStorage rather than a backend store, for the same reason as CZNA-593's ownership
 * filter: mod-czna-users has no per-user preference store. The key carries the user id, so
 * two supervisors on one machine never clear each other's flag. The cost — the marker does
 * not follow the user to another machine — can only produce a false "new" there, never hide
 * a new item, which is the right side to err on for a daily review duty.
 */
var STORAGE_KEY_PREFIX = 'czna:review-queue:lastSeen:';
export var reviewQueueLastSeenKey = function (userId) {
    return "".concat(STORAGE_KEY_PREFIX).concat(userId);
};
var storage = function () {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            return window.localStorage;
        }
    }
    catch (_a) {
        // SecurityError — fall through.
    }
    return null;
};
/**
 * The instant in microseconds. `Date.parse` keeps only milliseconds, but the server sends
 * `waitingSince` with microseconds, so an item arriving within the same millisecond as the
 * marker would otherwise compare equal and stay hidden.
 */
var toMicros = function (value) {
    var _a, _b;
    if (!value)
        return null;
    var millis = Date.parse(value);
    if (Number.isNaN(millis))
        return null;
    var fraction = (_b = (_a = /T[\d:]+\.(\d+)/.exec(value)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : '';
    var subMillis = Number(fraction.padEnd(6, '0').slice(3, 6));
    return millis * 1000 + subMillis;
};
/** The remembered marker, or null when nothing valid is stored for this user. */
export var readReviewQueueLastSeen = function (userId) {
    var store = storage();
    if (!userId || !store)
        return null;
    try {
        var raw = store.getItem(reviewQueueLastSeenKey(userId));
        return toMicros(raw) === null ? null : raw;
    }
    catch (_a) {
        return null;
    }
};
/**
 * Advances the marker to `newestWaitingSince`. An older or unparsable value is ignored, so
 * a stale cached count cannot move the marker backwards and resurrect a flag already seen.
 */
export var markReviewQueueSeen = function (userId, newestWaitingSince) {
    var store = storage();
    var next = toMicros(newestWaitingSince);
    if (!userId || !store || next === null)
        return;
    var current = toMicros(readReviewQueueLastSeen(userId));
    if (current !== null && current >= next)
        return;
    try {
        store.setItem(reviewQueueLastSeenKey(userId), newestWaitingSince);
    }
    catch (_a) {
        // QuotaExceeded / SecurityError — the flag simply stays on until storage works.
    }
};
/**
 * True when the queue holds an item newer than the last one this user saw. With no marker
 * at all the user has never opened the queue here, so any waiting item counts as new.
 */
export var hasNewSinceLastSeen = function (newestWaitingSince, lastSeen) {
    var newest = toMicros(newestWaitingSince);
    if (newest === null)
        return false;
    var seen = toMicros(lastSeen);
    return seen === null || newest > seen;
};
